/**
 * PluginTeamPanel — who on a workspace can Read or Publish one Fias-owned plugin.
 *
 * The workspace picker offers the org workspaces the signed-in admin belongs to
 * (same source as the header switcher). It is labelled "Org workspace", not
 * "Fias workspace": no canonical Fias-workspace id exists on the platform and an
 * admin can belong to several orgs, so the page must never assert that the
 * selection IS the Fias workspace. The chosen name is repeated in the heading.
 *
 * Roles: Read = `viewer`, Publish = `publisher`. There is no owner control here
 * on purpose (see platform-owned-admin.ts). Publish IS the write permission —
 * the plugin's source lives in the developer's repo — and the copy says so.
 *
 * Member ↔ collaborator matching is by username (see team-matching.ts). A 409
 * from either mutation means the roster moved under the admin; we refetch and
 * say so rather than retrying a decision made against stale data.
 *
 * Removal goes through `DELETE /admin/plugins/:id/team/:userId`, which
 * addresses the collaborator ROW by its stored id — so a row whose account
 * profile is gone can still be revoked here. A human `owner` row (possible on
 * a legacy first-party arche) is shown but not editable: this tool never
 * grants or removes team management.
 */

import * as React from 'react';
import { useHolderSwitcher } from '@/components/HolderSwitcher';
import { useWorkspaceMembers } from '@/auth/hooks/useWorkspaceTeam';
import { useToast } from '@/components/ui/use-toast';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { ApiError, type PluginMaintainer, type PluginTeamRole } from '@/lib/api';
import {
  usePluginMaintainers,
  useSetPluginTeamMember,
  useRemovePluginTeamMember,
  useInvalidateMaintainerState,
} from '@/contributor/hooks/usePlatformOwnedPlugins';
import { partitionTeam, describeUnresolvedAccount } from './team-matching';

type Access = 'none' | PluginTeamRole;

const ACCESS_LABEL: Record<Access, string> = {
  none: 'None',
  viewer: 'Read',
  publisher: 'Publish',
};

function accessOf(row: PluginMaintainer | null): Access {
  if (!row) return 'none';
  // A human owner row reads as Publish (it can publish) but its control is
  // disabled by the caller — owner is neither granted nor removed here.
  return row.role === 'viewer' ? 'viewer' : 'publisher';
}

/** Time-bound grants must be visible: "held" alone would hide that it lapses. */
function ExpiryBadge({ expiresAt }: { expiresAt: string | null }) {
  if (!expiresAt) return null;
  return (
    <Badge variant="outline" title={expiresAt}>
      expires {new Date(expiresAt).toLocaleDateString()}
    </Badge>
  );
}

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : 'unknown error';
}

interface AccessControlProps {
  value: Access;
  onChange: (next: Access) => void;
  disabled?: boolean;
  pending?: boolean;
  /** Accessible name for the radiogroup — the person it controls. */
  label: string;
}

/** Three-way None · Read · Publish control, rendered as a radiogroup of buttons. */
function AccessControl({ value, onChange, disabled, pending, label }: AccessControlProps) {
  return (
    <div role="radiogroup" aria-label={`Access for ${label}`} className="inline-flex gap-1">
      {(['none', 'viewer', 'publisher'] as const).map((option) => (
        <Button
          key={option}
          type="button"
          role="radio"
          aria-checked={value === option}
          size="sm"
          variant={value === option ? 'default' : 'outline'}
          disabled={disabled || pending}
          onClick={() => {
            if (option !== value) onChange(option);
          }}
        >
          {ACCESS_LABEL[option]}
        </Button>
      ))}
      {pending && <span className="sr-only">Saving…</span>}
    </div>
  );
}

interface PluginTeamPanelProps {
  archeId: string;
  pluginName: string;
}

export function PluginTeamPanel({ archeId, pluginName }: PluginTeamPanelProps) {
  const { toast } = useToast();
  const switcher = useHolderSwitcher({ defaultToPersonal: false });
  const orgOptions = React.useMemo(
    () => switcher.options.filter((o) => o.holderType === 'org'),
    [switcher.options],
  );

  // Default to the header's org selection when it is one; otherwise the first
  // org. Kept as local state so changing it here never moves the header chip.
  const [holderId, setHolderId] = React.useState<string | null>(null);
  React.useEffect(() => {
    if (holderId && orgOptions.some((o) => o.holderId === holderId)) return;
    const fromHeader = orgOptions.find((o) => o.holderId === switcher.selectedHolderId);
    setHolderId(fromHeader?.holderId ?? orgOptions[0]?.holderId ?? null);
  }, [orgOptions, switcher.selectedHolderId, holderId]);

  const selectedWorkspace = orgOptions.find((o) => o.holderId === holderId) ?? null;

  const rosterQuery = usePluginMaintainers(archeId);
  const membersQuery = useWorkspaceMembers(holderId);
  const setMember = useSetPluginTeamMember(archeId);
  const removeMember = useRemovePluginTeamMember(archeId);
  const invalidate = useInvalidateMaintainerState(archeId);

  // One in-flight change at a time, keyed by the person it targets, so a slow
  // save disables only that row.
  const [pendingKey, setPendingKey] = React.useState<string | null>(null);
  const [rowErrors, setRowErrors] = React.useState<Record<string, string>>({});

  const { matrix, others, platformOwner } = React.useMemo(
    () => partitionTeam(membersQuery.data ?? [], rosterQuery.data?.maintainers ?? []),
    [membersQuery.data, rosterQuery.data?.maintainers],
  );

  const applyChange = (
    key: string,
    personLabel: string,
    next: Access,
    identifier: string,
    rosterRow: PluginMaintainer | null,
  ) => {
    setPendingKey(key);
    setRowErrors((prev) => ({ ...prev, [key]: '' }));

    const onError = (error: unknown) => {
      if (error instanceof ApiError && error.status === 409) {
        // The roster moved under us (or the target is the Fias owner row).
        // Show the server's state before the admin re-chooses.
        invalidate();
        toast({
          title: 'Team changed elsewhere; refreshed',
          description: error.message,
          variant: 'destructive',
        });
        return;
      }
      setRowErrors((prev) => ({ ...prev, [key]: errorMessage(error) }));
    };
    const onSuccess = () => {
      toast({
        title: next === 'none' ? `Removed ${personLabel}` : `${personLabel}: ${ACCESS_LABEL[next]}`,
        description: pluginName,
      });
    };
    const onSettled = () => setPendingKey((k) => (k === key ? null : k));

    if (next === 'none') {
      if (!rosterRow) {
        onSettled();
        return;
      }
      // Address the roster row by its id — cannot go stale after a rename.
      removeMember.mutate(rosterRow.userId, { onError, onSuccess, onSettled });
      return;
    }
    setMember.mutate({ identifier, role: next }, { onError, onSuccess, onSettled });
  };

  return (
    <div className="space-y-5 rounded-md border bg-muted/30 p-4" data-testid="plugin-team-panel">
      <div className="space-y-1">
        <Label htmlFor={`workspace-${archeId}`}>Org workspace</Label>
        <select
          id={`workspace-${archeId}`}
          className="block w-full max-w-md rounded-md border border-input bg-background px-3 py-2 text-sm"
          value={holderId ?? ''}
          onChange={(e) => setHolderId(e.target.value || null)}
          disabled={orgOptions.length === 0}
        >
          {orgOptions.length === 0 && <option value="">No org workspace available</option>}
          {orgOptions.map((o) => (
            <option key={o.holderId} value={o.holderId}>
              {o.displayName}
            </option>
          ))}
        </select>
        <p className="text-xs text-muted-foreground">
          Pick the Fias workspace to staff from. Only workspaces you belong to are listed.
        </p>
      </div>

      <div className="space-y-1 text-xs text-muted-foreground">
        <p>
          <span className="font-medium text-foreground">Read</span> — can see this plugin&apos;s
          team. <span className="font-medium text-foreground">Publish</span> — can push updates with{' '}
          <code>fias-dev submit</code>. Publish is the &quot;write&quot; permission: the
          plugin&apos;s source lives in the developer&apos;s repo, so there is no separate one. Team
          management stays here in the Dev Console.
        </p>
        <p>
          Taking Publish away from someone who was the last member of a workspace able to publish
          this plugin stops that workspace&apos;s asset-library link qualifying at the next publish.
        </p>
      </div>

      {switcher.isLoading ? (
        <Skeleton className="h-10 w-full" data-testid="team-panel-loading" />
      ) : orgOptions.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          You are not a member of any org workspace, so there is nobody to pick from. Join the Fias
          workspace first, then come back here.
        </p>
      ) : rosterQuery.isError ? (
        <p role="alert" className="text-sm text-destructive">
          Could not load this plugin&apos;s team: {errorMessage(rosterQuery.error)}
        </p>
      ) : membersQuery.isError ? (
        <p role="alert" className="text-sm text-destructive">
          Could not load the workspace roster: {errorMessage(membersQuery.error)}
        </p>
      ) : rosterQuery.isLoading || membersQuery.isLoading ? (
        <div className="space-y-2" data-testid="team-panel-loading">
          <Skeleton className="h-8 w-full" />
          <Skeleton className="h-8 w-full" />
        </div>
      ) : (
        <>
          <section aria-labelledby={`members-${archeId}`} className="space-y-2">
            <h4 id={`members-${archeId}`} className="text-sm font-medium">
              Members of {selectedWorkspace?.displayName ?? 'the workspace'}
            </h4>
            {matrix.length === 0 ? (
              <p className="text-sm text-muted-foreground">This workspace has no active members.</p>
            ) : (
              <ul className="divide-y rounded border bg-background">
                {matrix.map(({ member, roster }) => {
                  const key = `member:${member.membershipId}`;
                  const personLabel =
                    member.displayName ?? (member.username ? `@${member.username}` : 'this member');
                  const unresolved = !member.username;
                  return (
                    <li
                      key={member.membershipId}
                      className="flex flex-col gap-2 p-3 sm:flex-row sm:items-center sm:justify-between"
                    >
                      <div className="min-w-0 space-y-0.5">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="font-medium">{personLabel}</span>
                          {member.username && (
                            <span className="text-xs text-muted-foreground">
                              @{member.username}
                            </span>
                          )}
                          <Badge variant="outline">{member.role}</Badge>
                          <ExpiryBadge expiresAt={roster?.expiresAt ?? null} />
                        </div>
                        {unresolved && (
                          <p className="text-xs text-destructive">
                            Account could not be resolved; access cannot be changed here.
                          </p>
                        )}
                        {rowErrors[key] && (
                          <p role="alert" className="text-xs text-destructive">
                            {rowErrors[key]}
                          </p>
                        )}
                      </div>
                      <AccessControl
                        label={personLabel}
                        value={accessOf(roster)}
                        disabled={unresolved}
                        pending={pendingKey === key}
                        onChange={(next) =>
                          applyChange(key, personLabel, next, member.username ?? '', roster)
                        }
                      />
                    </li>
                  );
                })}
              </ul>
            )}
          </section>

          <section aria-labelledby={`others-${archeId}`} className="space-y-2">
            <h4 id={`others-${archeId}`} className="text-sm font-medium">
              Other collaborators
            </h4>
            {platformOwner && (
              <p className="text-xs text-muted-foreground">
                <span className="font-medium text-foreground">Fias</span> · owner — the platform
                account owns this plugin; its row is not editable.
              </p>
            )}
            {others.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                No collaborators outside {selectedWorkspace?.displayName ?? 'this workspace'}.
              </p>
            ) : (
              <ul className="divide-y rounded border bg-background">
                {others.map((row) => {
                  const key = `other:${row.userId}`;
                  const personLabel = row.username
                    ? `@${row.username}`
                    : describeUnresolvedAccount(row.userId);
                  const isOwner = row.role === 'owner';
                  return (
                    <li
                      key={row.userId}
                      className="flex flex-col gap-2 p-3 sm:flex-row sm:items-center sm:justify-between"
                    >
                      <div className="min-w-0 space-y-0.5">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="font-medium">{personLabel}</span>
                          <Badge variant="secondary">{row.role}</Badge>
                          <ExpiryBadge expiresAt={row.expiresAt} />
                        </div>
                        {isOwner && (
                          <p className="text-xs text-muted-foreground">
                            Owner — has team management. This tool does not grant or remove
                            ownership; change it through the arche&apos;s own team settings.
                          </p>
                        )}
                        {!row.username && (
                          <p className="text-xs text-muted-foreground">
                            Account could not be resolved; changes address the collaborator row
                            directly.
                          </p>
                        )}
                        {rowErrors[key] && (
                          <p role="alert" className="text-xs text-destructive">
                            {rowErrors[key]}
                          </p>
                        )}
                      </div>
                      <AccessControl
                        label={personLabel}
                        value={accessOf(row)}
                        disabled={isOwner}
                        pending={pendingKey === key}
                        onChange={(next) => applyChange(key, personLabel, next, row.userId, row)}
                      />
                    </li>
                  );
                })}
              </ul>
            )}
          </section>
        </>
      )}
    </div>
  );
}
